import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { HelmetProvider } from "react-helmet-async";
import { useCmsPreviewBridge } from "./hooks/useCmsPreviewBridge";
import { lazy, Suspense } from "react";
import LandingPage from "./pages/LandingPage";

// Lazy routes keep the landing page's critical path small
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

// Service Detail Pages
const MarkteintrittPage = lazy(() => import("./pages/services/MarkteintrittPage"));
const MarktzugangPage = lazy(() => import("./pages/services/MarktzugangPage"));
const OperativeUmsetzungPage = lazy(() => import("./pages/services/OperativeUmsetzungPage"));
const MarktvalidierungPage = lazy(() => import("./pages/services/MarktvalidierungPage"));
const VollzugangEuropaPage = lazy(() => import("./pages/services/VollzugangEuropaPage"));
const ZusatzleistungenPage = lazy(() => import("./pages/services/ZusatzleistungenPage"));

const queryClient = new QueryClient();

function ServicesRedirect() {
  const path = window.location.pathname.replace("/services/", "/leistungen/");
  return <Navigate to={path} replace />;
}

function PreviewBridge() {
  useCmsPreviewBridge();
  return null;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <PreviewBridge />
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/app" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            
            {/* Service Detail Pages */}
            <Route path="/leistungen/markteintritt" element={<MarkteintrittPage />} />
            <Route path="/leistungen/marktzugang" element={<MarktzugangPage />} />
            <Route path="/leistungen/operative-umsetzung" element={<OperativeUmsetzungPage />} />
            <Route path="/leistungen/marktvalidierung" element={<MarktvalidierungPage />} />
            <Route path="/leistungen/vollzugang-europa" element={<VollzugangEuropaPage />} />
            <Route path="/leistungen/zusatzleistungen" element={<ZusatzleistungenPage />} />
            
            {/* Alte /services/* Adressen auf die aktuellen /leistungen/* Seiten umleiten */}
            <Route path="/services/:slug" element={<ServicesRedirect />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
